// @pages/account/PasswordResetComplete.page.tsx

import React, { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { renderRoute, routeLogin, routePasswordResetRequest } from "@navigation/typedRoutes";
import { useAuthenticationClient } from "@contexts/AuthenticationClient.context";
import { useLoadingController } from "@contexts/Loading.context";
import { useIoOptionState } from "@utils/useIoOptionState";
import { useRunTaskWithError } from "@hooks/effects/useTaskEffect";
import PasswordResetCompleteView from "@view/authentication/PasswordResetComplete.view";

import { ok, type Ok } from "@ps-bridge/ok";
import { makePasswordResetToken } from "@ps-bridge/models/brands/passwordResetToken";
import { mkNavigate, mkTaskRunner } from "@ps-bridge/capabilities";
import type { PasswordResetCompleteRequest, PasswordResetError } from "@ps-bridge/models/core/dataContracts";
import type { TaskEitherTs, TaskTs } from "@ps-bridge/interop";
import { ioEffect, taskEitherFromEffect } from "@utils/effects/branded";
import * as PSOption from "@utils/Option";
import { getParamOption } from "@utils/searchParams";
import type { PasswordResetCompleteKernel } from "@ps-bridge/contracts/passwordResetComplete";
import { mkPasswordResetCompleteController } from "@ps-bridge/controllers/passwordResetComplete";
import { buildPasswordResetCompleteViewProps } from "@ps-bridge/passwordResetComplete.program";

const PasswordResetCompletePage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const authenticationClient = useAuthenticationClient();
    const loading = useLoadingController();
    const { read: password, setSome: setPassword, setMaybe: setPasswordMaybe } = useIoOptionState<string>(PSOption.none());
    const { read: confirmPassword, setSome: setConfirmPassword, setMaybe: setConfirmPasswordMaybe } = useIoOptionState<string>(PSOption.none());
    const runTaskWithError = useRunTaskWithError();

    const tokenValue = useMemo(
        () => getParamOption(searchParams, "token"),
        [searchParams],
    );
    const token = useMemo(
        () => PSOption.map(makePasswordResetToken)(tokenValue),
        [tokenValue],
    );
    const logoUri = PSOption.some("/assets/ouroboros_logo_flat.png");
    const runTaskWithErrorIo = useCallback(
        (task: TaskTs<Ok>) =>
            ioEffect(() => {
                runTaskWithError(task).then(() => ok);
                return ok;
            }),
        [runTaskWithError],
    );
    const taskRunner = useMemo(() => mkTaskRunner(runTaskWithErrorIo), [runTaskWithErrorIo]);
    const completePasswordReset = useMemo(
        () =>
            (request: PasswordResetCompleteRequest): TaskEitherTs<PasswordResetError, Ok> =>
                taskEitherFromEffect(
                    authenticationClient.completePasswordReset(request),
                    (error) => error as PasswordResetError,
                ),
        [authenticationClient],
    );
    const onSuccess = useMemo(
        () =>
            ioEffect(() => {
                navigate(renderRoute(routeLogin));
                return ok;
            }),
        [navigate],
    );
    const controller = useMemo(
        () =>
            mkPasswordResetCompleteController({
                token,
                password: {
                    read: password,
                    setSome: setPassword,
                    setMaybe: setPasswordMaybe,
                },
                confirmPassword: {
                    read: confirmPassword,
                    setSome: setConfirmPassword,
                    setMaybe: setConfirmPasswordMaybe,
                },
                loading,
                completePasswordReset,
                taskRunner,
                onSuccess,
            }),
        [
            completePasswordReset,
            confirmPassword,
            loading,
            onSuccess,
            password,
            setConfirmPassword,
            setConfirmPasswordMaybe,
            setPassword,
            setPasswordMaybe,
            taskRunner,
            token,
        ],
    );
    const kernel: PasswordResetCompleteKernel = useMemo(
        () => ({
            controller,
            loading,
            navigate: mkNavigate((route) =>
                ioEffect(() => {
                    navigate(renderRoute(route));
                    return ok;
                }),
            ),
            form: {
                password: {
                    read: password,
                    setSome: setPassword,
                    setMaybe: setPasswordMaybe,
                },
                confirmPassword: {
                    read: confirmPassword,
                    setSome: setConfirmPassword,
                    setMaybe: setConfirmPasswordMaybe,
                },
            },
        }),
        [
            confirmPassword,
            controller,
            loading,
            navigate,
            password,
            setConfirmPassword,
            setConfirmPasswordMaybe,
            setPassword,
            setPasswordMaybe,
        ],
    );
    const viewProps = useMemo(
        () =>
            buildPasswordResetCompleteViewProps({
                kernel,
                logoUri,
                requestResetRoute: routePasswordResetRequest,
                token,
            }),
        [kernel, logoUri, token],
    );

    return (
        <PasswordResetCompleteView
            {...viewProps}
        />
    );
};

export default PasswordResetCompletePage;
